import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button, IconButton, Modal } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { openBillingInformationPage } from "app/common/hrefs";
import {
    NoTouchPurchasePaymentFailureCloseButtonContainer,
    NoTouchPurchasePaymentFailureContactButton,
    NoTouchPurchasePaymentFailureContactLinkText,
    NoTouchPurchasePaymentFailureContainer,
    NoTouchPurchasePaymentFailureHelpLine,
    NoTouchPurchasePaymentFailureIllustration,
} from "app/components/no-touch-purchase-modal/confirmation-modal/payment-failure/StyledNoTouchPurchasePaymentFailure";
import { NO_TOUCH_PURCHASE_TRANSLATIONS } from "app/components/no-touch-purchase-modal/translations";
import { openHooksModalV5 } from "app/services/ModalService";
import { allTrackers } from "app/services/track/track";

import paymentFailureIllustration from "./images/payment_failure.svg";

const PAYMENT_FAILURE_EVENT_NAME = "one click purchase/purchase payment failure";

const paymentFailureModalStyle: ReactModal.Styles = {
    content: {
        width: "524px",
        minHeight: "436px",
        border: 0,
        borderRadius: "8px",
    },
};

interface INoTouchPurchasePaymentFailureProps {
    onClose: () => void;
}

export const NoTouchPurchasePaymentFailure = ({ onClose }: INoTouchPurchasePaymentFailureProps) => {
    const { translate } = useTranslate();

    const handleUpdatePaymentMethodClick = () => {
        allTrackers.dispatchTrackEvent("popup", "click", `${PAYMENT_FAILURE_EVENT_NAME}/update payment method`);
        openBillingInformationPage();
    };

    const handleContactBillingClick = () => {
        allTrackers.dispatchTrackEvent("popup", "click", `${PAYMENT_FAILURE_EVENT_NAME}/contact billing`);
        openHooksModalV5("ContactUs", undefined, "ContactUsPaymentFailure", "GetLoading");
        onClose();
    };

    return (
        <Modal
            customStyles={paymentFailureModalStyle}
            isOpen={true}
            onRequestClose={onClose}
            data-automation="no-touch-purchase-payment-failed-modal"
        >
            <NoTouchPurchasePaymentFailureCloseButtonContainer>
                <IconButton
                    type="flat"
                    iconName="clear"
                    iconSize="sm"
                    placement="left"
                    onClick={onClose}
                    data-automation="no-touch-purchase-payment-failed-modal-close"
                />
            </NoTouchPurchasePaymentFailureCloseButtonContainer>

            <NoTouchPurchasePaymentFailureContainer>
                <NoTouchPurchasePaymentFailureIllustration
                    src={paymentFailureIllustration}
                    alt={translate(NO_TOUCH_PURCHASE_TRANSLATIONS.paymentFailure.illustrationAlt)}
                />

                <Flex
                    flexDirection="column"
                    alignItems="center"
                    gap="24px"
                    width="100%"
                >
                    <Text
                        fontVariant="h4"
                        color={colorsPalettes.carbon[500]}
                        textAlign="center"
                    >
                        {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.paymentFailure.title)}
                    </Text>

                    <Text
                        fontVariant="body1"
                        color={colorsPalettes.carbon[300]}
                        textAlign="center"
                    >
                        {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.paymentFailure.description)}
                    </Text>

                    <Flex
                        flexDirection="column"
                        alignItems="center"
                        gap="24px"
                    >
                        <Button
                            type="primary"
                            onClick={handleUpdatePaymentMethodClick}
                            data-automation="no-touch-purchase-payment-failed-modal-update-payment"
                        >
                            {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.paymentFailure.updatePaymentMethod)}
                        </Button>

                        <NoTouchPurchasePaymentFailureHelpLine>
                            <Text
                                fontVariant="body1"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.paymentFailure.needHelp)}{" "}
                            </Text>
                            <NoTouchPurchasePaymentFailureContactButton
                                type="button"
                                onClick={handleContactBillingClick}
                                data-automation="no-touch-purchase-payment-failed-modal-contact-billing"
                            >
                                <NoTouchPurchasePaymentFailureContactLinkText
                                    fontVariant="body1"
                                    color={colorsPalettes.blue[400]}
                                >
                                    {translate(NO_TOUCH_PURCHASE_TRANSLATIONS.paymentFailure.contactBilling)}
                                </NoTouchPurchasePaymentFailureContactLinkText>
                            </NoTouchPurchasePaymentFailureContactButton>
                        </NoTouchPurchasePaymentFailureHelpLine>
                    </Flex>
                </Flex>
            </NoTouchPurchasePaymentFailureContainer>
        </Modal>
    );
};
